import { USER_LIST_CSV_HEADER } from '@/features/mold/utils';
import type {
  UserModel,
  UsersForBulkUpdateSchema,
} from '@/features/tenant-manager/models';
import Papa from 'papaparse';
import type { z } from 'zod';

export type UsersData = z.infer<typeof UsersForBulkUpdateSchema>;

export const createUsersData = (
  csvData: (string | number)[][],
): UsersData | null => {
  const headerRow = csvData[0];
  if (headerRow) {
    const nameIndex = headerRow.indexOf(USER_LIST_CSV_HEADER.name);
    const emailIndex = headerRow.indexOf(USER_LIST_CSV_HEADER.email);
    const organizationIndexies = headerRow
      .map((label, i) =>
        label === USER_LIST_CSV_HEADER.organizationName ||
        label === USER_LIST_CSV_HEADER.organizationCode
          ? i
          : null,
      )
      .filter((i): i is NonNullable<typeof i> => i !== null);

    const bodyRows = csvData.filter((_, i) => i > 0);

    if (nameIndex < 0 || emailIndex < 0 || bodyRows.length <= 0) {
      return null;
    }

    const users = bodyRows.map((row) => {
      const organizations = row
        .filter((_, i) => organizationIndexies.includes(i))
        .reduce(
          (
            arr: {
              name: string;
              flattenExternalSystemCode: string;
            }[],
            cell: string | number,
            i: number,
          ) => {
            if (i % 2 === 0) {
              arr.push({
                name: cell.toString(),
                flattenExternalSystemCode: '',
              });
            } else {
              const last = arr.pop();
              if (last) {
                arr.push({
                  name: last.name,
                  flattenExternalSystemCode: cell.toString(),
                });
              }
            }
            return arr;
          },
          [],
        )
        .filter(
          ({ name, flattenExternalSystemCode }) =>
            name.trim() !== '' || flattenExternalSystemCode.trim() !== '',
        );

      return {
        name: row[nameIndex]?.toString() ?? '',
        email: row[emailIndex]?.toString() ?? '',
        organizations,
      };
    });

    const isExistInvalidUsers = users.some(
      ({ name, email, organizations }) =>
        name.trim() === '' ||
        email.trim() === '' ||
        organizations.some(
          ({ name, flattenExternalSystemCode }) =>
            name.trim() === '' || flattenExternalSystemCode.trim() === '',
        ),
    );

    if (isExistInvalidUsers) {
      return null;
    }

    return users;
  }
  return null;
};

const _readFileAsText = (
  file: File,
  callbacks: {
    onError: () => void;
    onSuccess: (result: Papa.ParseResult<(string | number)[]>) => void;
  },
) => {
  const reader = new FileReader();
  reader.readAsText(file);
  reader.onloadend = async () => {
    if (typeof reader.result !== 'string') {
      callbacks.onError();
      return;
    }

    callbacks.onSuccess(
      Papa.parse<Array<string | number>>(reader.result, {
        skipEmptyLines: true,
      }),
    );
  };
};

export const parseUserOptionLabel = (
  user: z.infer<typeof UserModel>,
): string => {
  return `${user.name}${
    user.organizations && user.organizations.length > 0
      ? ` | ${user.organizations.map((d) => d.name).join(', ')}`
      : ''
  }`;
};
